<template>
  <DialogHeader>
    <template #right>
      <div
        v-if="!loading"
        class="font-medium text-green-900 cursor-pointer"
        :class="{ 'opacity-40 pointer-events-none': isBasic }"
        @click="handleClick"
      >
        Save
      </div>
      <Icon
        v-else
        name="Loading"
        :size="14"
        class="stroke-gray-800 animate-spin"
      />
    </template>
    <div>Default privacy controls</div>
    <Pill v-if="isBasic" class="ml-3" success>Pro only</Pill>
  </DialogHeader>
  <DialogContent>
    <UpgradeToProCallout v-if="isBasic" class="mb-5">
      Enable privacy/invite controls and other features
    </UpgradeToProCallout>

    <div :class="containerClasses">
      <div class="mb-7">
        Choose the default visibility for copyrights you add to Dacr. This will
        not affect any files you have already added.
      </div>

      <div class="flex justify-between mb-[16px]">
        <div class="font-primary text-gray-800">
          <div
            class="flex items-center font-semibold font-primary text-[14px] leading-[18px] mb-1"
          >
            <Icon :size="20" name="Lock" class="mr-2 stroke-gray-800" />
            <div>Make file private</div>
          </div>
          <div class="max-w-[430px]">
            Only you or those you invite can see the files you add and their
            descriptions.
          </div>
        </div>
        <Toggle v-model="isPrivate" :disabled="isBasic" />
      </div>
    </div>
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      :disabled="loading || isBasic"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton
      class="w-1/2 tablet-h:w-auto"
      :disabled="isBasic"
      :loading="loading"
      primary
      @click="handleClick"
    >
      Save
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import { Icon, Pill, Toggle, UiButton } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { computed, ref } from 'vue'
import {
  FilePrivacy,
  UserPermission,
  useUpdateAccountDefaultFilePrivacyMutation,
  type User,
} from '@/shared/graphql/legacy/generated'
import UpgradeToProCallout from '@/features/billing/components/UpgradeToProCallout.vue'
import { ToastType, useToastStore } from '@/shared/stores/toast'

const { close } = useDialog()

const props = defineProps<{
  user: User
}>()

const { mutate: updateAccountDefaultFilePrivacy, loading } =
  useUpdateAccountDefaultFilePrivacyMutation()

const toastStore = useToastStore()

const isBasic = computed(
  () => !props.user.permissions.includes(UserPermission.Pro)
)

const containerClasses = computed(() => ({
  'pointer-events-none opacity-50': isBasic.value,
}))

const isPrivate = ref(props.user.defaultFilePrivacy === FilePrivacy.Private)

const handleClick = async () => {
  try {
    await updateAccountDefaultFilePrivacy(
      {
        isPrivate: isPrivate.value,
      },
      {
        optimisticResponse: {
          __typename: 'Mutation',
          updateAccountDefaultFilePrivacy: {
            __typename: 'User',
            id: props.user.id,
            defaultFilePrivacy: isPrivate.value
              ? FilePrivacy.Private
              : FilePrivacy.Public,
          },
        },
      }
    )

    toastStore.showToast({
      type: ToastType.Success,
      content: 'Default file privacy updated successfully',
    })

    close()
  } catch (error) {
    toastStore.showToast({
      type: ToastType.Error,
      content: 'Could not update default file privacy',
    })
  }
}
</script>
